import { isPlatformApp } from '@/apps/apps.utils';
import { ALL_PLATFORM, ALL_AGG_ID, WEB_LIKE_AGG_ID } from '@/charts/utils/apps.utils';
import { PageParameterId, regionEvalExpression, APP_SPECIFIC_AUTO_METADATA_IDS } from '@pendo/middleware';
import { hasSubscriptionFlag } from '@/data/flags/local-flags';
import { FilterType } from '@/types/analytics/analytics.types';
import ListColumn from '@/utils/list-column';
import { NamedAggregation } from '@/utils/named-aggregation.utils';
import { isDefaultSegment } from '@/utils/segments.js';
import { operators as o } from '@pendo/aggregations';
import { GuideExperimentAggUtils, GuideExperimentVisitorsAgg } from '@pendo/services/GuideExperiments';
import { b64EncodeUnicode } from '@utils/http';
import momentWrapper from '@utils/moment-wrapper';
import compact from 'lodash/compact';
import filter from 'lodash/filter';
import get from 'lodash/get';
import has from 'lodash/has';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isObject from 'lodash/isObject';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import { isoDatesToTimestamps } from './iso-dates-to-timestamps';
import {
    parseAppSpecificAutoMetadataFilterPipeline,
    parseApplicationUsageFilterPipeline,
    parseCounterFilterPipeline,
    parseEmailFilterPipeline,
    parseGuideFilterPipeline,
    parseJourneyFilterPipeline,
    parseSurveyFilterPipeline,
    surveyAggFriendlyTime,
    surveyAggFriendlyQuestionId,
} from './pipeline-filters';
import { buildCompoundPipelines, isCompoundSegment } from './compound-segment';

/* eslint-disable eqeqeq*/
// major issue when attempting to fix triple equals that breaks test (guessing null is used)
function createFilterString (filter, kind, useEventPropertyData = false, needsCustomFilterString = false) {
    if (filter.schema === 'counter') {
        if (filter.id === 'idea') {
            const { field } = filter;

            let filterString = '';

            if (filter.hasVoted) {
                filterString = `${field}.revoked == false`;

                if (filter.importance === 'positive') {
                    filterString += ` && ${field}.strength > 0`;
                } else if (filter.importance !== 'any') {
                    filterString += ` && ${field}.strength == ${filter.importance}`;
                }

                if (filter.time === 'withinLast') {
                    filterString += ` && ${field}.lastModifiedAt > dateAdd(startOfPeriod("daily", now()), ${-filter.count}, "${
                        filter.granularity
                    }")`;
                }
                if (filter.time === 'between') {
                    filterString += ` && ${field}.lastModifiedAt > ${
                        filter.first
                    } && ${field}.lastModifiedAt < ${makeBetweenEndInclusive(filter.last)}`;
                }
            } else {
                filterString = `isNil(${field})`;

                if (filter.time === 'withinLast') {
                    filterString += ` || ${field}.lastModifiedAt < dateAdd(startOfPeriod("daily", now()), ${-filter.count}, "${
                        filter.granularity
                    }")`;
                }
                if (filter.time === 'between') {
                    filterString += ` || ${field}.lastModifiedAt < ${
                        filter.first
                    } || ${field}.lastModifiedAt > ${makeBetweenEndInclusive(filter.last)}`;
                }
            }

            return `(${filterString})`;
        }

        return parseCounterFilterPipeline(filter).filter;
    }

    if (filter.schema === 'guideElement' || filter.schema === 'emailElement') {
        const { field, clicked, fact, value } = filter;

        switch (clicked) {
            case 'atleast':
                return `${field} >= ${value}`;
            case 'atmost':
                return `(${field} <= ${value} || isNull(${field}))`;
            case 'since':
            case 'withinLast':
            case 'ever':
            default:
                return `(${field} ${fact === 'clicked' ? '> 0' : `<= 0 || isNull(${field})`})`;
        }
    }

    if (filter.schema === 'deviceType') {
        const appIdString = getAutoPropAppIdString(filter.appId);
        const field = `userAgent${appIdString}.deviceType`;

        switch (filter.operator) {
            case '==':
            case '!=':
                return `${field}${filter.operator}"${filter.value}"`;
            case 'empty':
                return `isEmpty(${field})`;
            case '!empty':
                return `!isEmpty(${field})`;
            case 'contains':
                return `contains(${field},"${filter.value}")`;
            case '!contains':
                return `!contains(${field},"${filter.value}")`;
        }
    }

    if (filter.schema === 'guide') {
        if (filter.type == 'guideExperiment') {
            return `!isNil(experimentVisitorId${filter.filterIndex})`;
        }

        return parseGuideFilterPipeline(filter).filter;
    }

    if (filter.schema === 'journey') {
        return parseJourneyFilterPipeline(filter).filter;
    }

    if (filter.schema === 'email') {
        return parseEmailFilterPipeline(filter).filter;
    }

    if (filter.schema === 'application') {
        return parseApplicationUsageFilterPipeline(filter).filter;
    }

    if (filter.schema === 'survey') {
        return parseSurveyFilterPipeline(filter).filter;
    }

    if (filter.schema === 'expression') {
        const field = formatField(filter, kind, useEventPropertyData);

        if (filter.value === false) {
            return field.split(' == ').join(' != ');
        }

        return field;
    }

    if (APP_SPECIFIC_AUTO_METADATA_IDS.includes(filter.id)) {
        const field = formatField(filter, kind);

        return parseAppSpecificAutoMetadataFilterPipeline({ ...filter, field }).filter;
    }

    if (filter.field) {
        if (needsCustomFilterString && filter.id === 'utm') {
            return `(${filter.field} >= 1)`;
        }
        const field = formatField(filter, kind, useEventPropertyData);
        let value = JSON.stringify(filter.value);
        if (!value && filter.id === 'utm') {
            value = "''";
        }

        if (filter.schema === 'deviceModel') {
            switch (filter.operator) {
                case 'contains':
                case '!contains':
                case 'startsWith':
                case '!startsWith':
                    return `${filter.operator}(toLowerCase(${field}),${value})`;
                case '==':
                case '!=':
                    return `toLowerCase(${field})${filter.operator}${value}`;
            }
        }

        if (filter.schema === 'feedbackRequest') {
            return `${field} > 0`;
        }

        if (filter.schema === 'boolean' && filter.value === false) {
            return `(isNil(${field})||${field}==false)`;
        }

        if (/^!?withinLast$/.test(filter.operator)) {
            const inverse = filter.operator.indexOf('!') === 0;

            const expression = [
                field,
                inverse ? '>=' : '<=',
                'now()',
                inverse ? '||' : '&&',
                field,
                inverse ? '<=' : '>=',
                'dateAdd(startOfPeriod("daily", now()),',
                -filter.count,
                ',"',
                filter.granularity,
                '")',
            ].join('');

            return `(${expression})`;
        }

        if (/^!?withinNext$/.test(filter.operator)) {
            const inverse = filter.operator.indexOf('!') === 0;

            const expression = [
                field,
                inverse ? '<=' : '>=',
                'now()',
                inverse ? '||' : '&&',
                field,
                inverse ? '>=' : '<=',
                'dateAdd(startOfPeriod("daily", now()),',
                filter.count,
                ',"',
                filter.granularity,
                '")',
            ].join('');

            return `(${expression})`;
        }

        if (filter.operator === 'between') {
            return `(${field}>=${filter.first}&&${field}<${makeBetweenEndInclusive(filter.last)})`;
        }

        if (filter.operator === 'empty') {
            return `isEmpty(${field})`;
        }

        if (filter.operator === '!empty') {
            return `!isEmpty(${field})`;
        }

        if (filter.operator === 'startsWith') {
            return `startsWith(${field},${value})`;
        }

        if (filter.operator === '!startsWith') {
            return `!startsWith(${field},${value})`;
        }

        if (filter.operator === 'responded') {
            return `!isNil(${field})`;
        }

        if (filter.operator === '!responded') {
            return `${filter.time === 'ever' ? '' : '!'}isNil(${field})`;
        }

        if (filter.operator === 'listContains') {
            return `!isNil(${field}) && !isNil(metadata.auto.firstvisit) && 0 != metadata.auto.firstvisit`;
        }

        if (filter.operator === '!listContains') {
            return `isNil(${field})`;
        }

        if (field && filter.operator && value) {
            // If using an event property with <=, automatically append >=1 as well. If this is not
            // done, you'll get back visitors with zero track/feature events included with the visitors with
            // events that contained the specified event property criteria
            if (/!?_eventProperty\d/.test(field) && filter.operator === '<=') {
                return `(${field}${filter.operator}${value} && ${field}>=1)`;
            }

            if (/pollResponse/.test(field) && filter.operator === '!=') {
                return `(${field}${filter.operator}${value} && !isNil(${field}))`;
            }

            if (/pollResponse/.test(field) && filter.operator === '!contains') {
                return `(!contains(${field},${value}) && !isNil(${field}))`;
            }

            return handleMultipleValuesInFilter(field, filter.operator, filter.value, filter.filterType);
        }

        return null; // bad `filter` flag...?
    }
}

function handleMultipleValuesInFilter (field, operator, value, filterType) {
    const comparisonValues = isArray(value) ? value : [value];
    let formattedValues;
    let commaSeparatedValues;
    if (filterType === FilterType.Location) {
        if (field === 'country') {
            formattedValues = comparisonValues
                .map((val) => {
                    const code = val;

                    return JSON.stringify(code);
                })
                .filter(Boolean);
            commaSeparatedValues = formattedValues.join(',');
        } else if (field === 'country_region') {
            formattedValues = comparisonValues
                .map((val) => {
                    const parts = val.split('-');
                    if (parts.length === 2) {
                        const countryCode = parts[0];
                        const regionCode = parts[1];
                        const code = `${countryCode.toUpperCase()}-${regionCode.toLowerCase()}`;

                        return JSON.stringify(code);
                    }

                    return undefined;
                })
                .filter(Boolean);
            commaSeparatedValues = formattedValues.join(',');
        }
    } else {
        formattedValues = comparisonValues.map((val) => JSON.stringify(val));
        commaSeparatedValues = formattedValues.join(',');
    }

    if (operator === '==') {
        if (formattedValues.length > 1) {
            // Equals one of the given values
            return `contains([${commaSeparatedValues}],${field})`;
        }

        return field + operator + formattedValues[0];
    }
    if (operator === '!=') {
        if (formattedValues.length > 1) {
            // Is not equal to ANY of the given values
            return `(!contains([${commaSeparatedValues}],${field}))`;
        }

        return field + operator + formattedValues[0];
    }

    if (operator === 'contains') {
        // Contains one of the given values
        return formattedValues.map((val) => `contains(${field},${val})`).join('||');
    }
    if (operator === '!contains') {
        // Does not contain ANY of the given values (De Morgan's Law)
        return formattedValues.map((val) => `(!contains(${field},${val}))`).join('&&');
    }

    return field + operator + JSON.stringify(value);
}

function formatField (filter, kind, useEventPropertyData) {
    const {
        eventPropertyName,
        eventPropertyField,
        automaticEventProperty,
        eventPropertyKind,
        eventPropertyGroup,
        filterType,
    } = filter;

    if (((eventPropertyField && has(filter, 'eventPropertyValue')) || eventPropertyName) && useEventPropertyData) {
        const eventPropIsSessionProp = filter.filterType === FilterType.Utm;
        if (eventPropIsSessionProp) {
            return `properties.__utm__.${eventPropertyField}`;
        }

        if (automaticEventProperty && eventPropertyField) {
            return `parsedUA.${eventPropertyField}`;
        }

        if (eventPropertyKind && eventPropertyGroup && eventPropertyField) {
            return `properties.__sg__.${eventPropertyKind.toLowerCase()}metadata.${eventPropertyGroup}__${eventPropertyField ||
                eventPropertyName}`;
        }

        if (eventPropertyField === 'region' && filterType === FilterType.Location) {
            return 'country_region';
        }

        if (eventPropertyField === 'country' && filterType === FilterType.Location) {
            return 'country';
        }

        if (eventPropertyField === PageParameterId && filterType === FilterType.PageParameter) {
            return PageParameterId;
        }

        if (eventPropertyField === 'server' && filterType === FilterType.EventTopLevel) {
            return 'server';
        }

        return `properties.${eventPropertyField || eventPropertyName}`;
    }
    const filterIsSessionProperty = filter.id === 'utm';
    if (filterIsSessionProperty) {
        return filter.type; // Type stores the filter string for session properties
    }

    return filter.field.replace(new RegExp(`^${kind}\\.`), 'metadata.');
}

function makeBetweenEndInclusive (last) {
    return momentWrapper(last)
        .add(1, 'days')
        .valueOf();
}

function isListContains (filter) {
    return /!?listContains/.test(filter.operator);
}

function fieldName (filter, tagName, index = 0) {
    if (filter.schema === 'aggregation') {
        const key = compact([
            filter.kind,
            filter.fact,
            filter.time,
            filter.count,
            filter.granularity,
            filter.first,
            filter.last,
        ]);

        if (filter.pageId) {
            key.unshift(`page${tagName}`);
        } else if (filter.featureId) {
            key.unshift(`feature${tagName}`);
        } else if (filter.trackTypeId) {
            key.unshift(`trackType${tagName}`);
        } else if (filter.guideElementId) {
            key.unshift(`guideElement${tagName}`);
        } else if (filter.emailElementId) {
            key.unshift(`emailElement${tagName}`);
        } else if (filter.id === 'utm') {
            // Ensure uniqueness per UTM subrule so OR clauses do not collapse
            key.unshift(`${tagName}_${index}`);
        } else if (filter.appId) {
            key.unshift(`app${tagName}`);
        }

        if ((filter.eventPropertyField && has(filter, 'eventPropertyValue')) || filter.eventPropertyName) {
            key.splice(1, 0, `eventProperty${index}`);
        }

        return key.join('_');
    }
    if (filter.schema === 'counter') {
        if (filter.pageId) {
            return `page${tagName}_itemSeen`;
        }
        if (filter.featureId) {
            return `feature${tagName}_itemSeen`;
        }
        if (filter.trackTypeId) {
            return `trackType${tagName}_itemSeen`;
        }
        if (filter.ideaId) {
            return `vocItem${tagName}_visitorVotes`;
        }
    } else if (isListContains(filter)) {
        return `tag${tagName}`;
    }
}

function pollSourceKey (source) {
    return `${source.kind}-${source.guideId}-${source.pollId}`;
}

function expandNpsResponseGroupFilters (filters, pollResponseSources) {
    return filters.map((filter) => {
        if (filter.or) {
            return {
                ...filter,
                or: expandNpsResponseGroupFilters(filter.or, pollResponseSources),
            };
        }

        if (filter.and) {
            return {
                ...filter,
                and: expandNpsResponseGroupFilters(filter.and, pollResponseSources),
            };
        }

        if (
            filter.schema === 'poll' &&
            filter.npsSegmentType === 'npsThemes' &&
            filter.npsResponseGroup &&
            filter.npsResponseGroup !== 'allResponseGroups'
        ) {
            const source = pollResponseSources[pollSourceKey(filter)];

            const responseGroups = {
                promoters: [{ field: source.response, operator: '>=', value: 9 }],
                detractors: [{ field: source.response, operator: '<=', value: 6 }],
                passives: [
                    { field: source.response, operator: '>=', value: 7 },
                    { field: source.response, operator: '<=', value: 8 },
                ],
            };

            const and = [filter, ...responseGroups[filter.npsResponseGroup]];

            return { and };
        }

        return filter;
    });
}

function expandTimeBoundPollFilters (filters, pollResponseSources) {
    return filters.map((filter) => {
        if (filter.or) {
            return {
                or: expandTimeBoundPollFilters(filter.or, pollResponseSources),
            };
        }

        if (filter.schema === 'poll' && filter.time !== 'ever') {
            const source = pollResponseSources[pollSourceKey(filter)];

            return {
                and: [
                    filter,
                    {
                        field: source.time,
                        operator: filter.time,
                        value: filter.first,
                        first: filter.first,
                        last: filter.last,
                        count: filter.count,
                        granularity: filter.granularity,
                    },
                ],
                invert: filter.operator === '!responded',
            };
        }

        return filter;
    });
}

function expandTimeBoundSurveyFilters (filters) {
    return filters.map((filter) => {
        if (filter.or) {
            return {
                or: expandTimeBoundSurveyFilters(filter.or),
            };
        }

        if (filter.schema === 'survey' && filter.time !== 'ever') {
            return {
                and: [
                    filter,
                    {
                        field: surveyAggFriendlyTime(filter),
                        operator: filter.time,
                        value: filter.first,
                        first: filter.first,
                        last: filter.last,
                        count: filter.count,
                        granularity: filter.granularity,
                    },
                ],
            };
        }

        return filter;
    });
}

function createEntireFilterString (filter, kind, needsCustomFilterString) {
    if (filter.and) {
        return `${filter.invert ? '!' : ''}(${filter.and
            .map((andFilter) => createEntireFilterString(andFilter, kind, needsCustomFilterString))
            .filter((str) => str) // to avoid falsy vals
            .join(' && ')})`;
    }
    if (filter.or) {
        return `${filter.invert ? '!' : ''}(${filter.or
            .map((orFilter) => createEntireFilterString(orFilter, kind, needsCustomFilterString))
            .filter((str) => str) // to avoid falsy vals
            .join(' || ')})`;
    }

    return createFilterString(filter, kind, false, needsCustomFilterString) || '';
}

function getAutoPropAppIdString (appId) {
    if (appId === -323232 || typeof appId === 'undefined') {
        return '__323232';
    }
    if (appId === '*') {
        return '';
    }

    return `_${appId}`;
}

function evalCompoundRule (rule) {
    return typeof rule === 'string' ? rule : `${rule.operator === '!=' ? '!' : ''}${rule.segmentId}`;
}

function createCompoundExpression (compounds) {
    return compounds
        .map((compound) => {
            if (Array.isArray(compound)) {
                const rule = `${compound.map(evalCompoundRule).join(' || ')}`;

                return compound.length > 1 ? `(${rule})` : rule;
            }

            return evalCompoundRule(compound);
        })
        .join(' && ');
}

function spawnPrimarySource (kinds, kind) {
    if (kinds?.includes('visitor')) {
        return o.pipeline(o.sources.visitors());
    }

    if (kinds?.includes('parentAccount') && kinds?.includes('account')) {
        return o.pipeline(o.sources.accounts({ parentAccounts: true, subAccounts: true }));
    }

    if (kind === 'parentAccount') {
        return o.pipeline(
            o.sources.accounts({ parentAccounts: true, subAccounts: false, blacklist: 'ignore' }),
            o.evalExpression('parentAccountId', 'parentAccountId(accountId)'),
        );
    }

    return o.pipeline(o.sources[`${kind}s`]());
}

export async function getPipelineProps (segment) {
    const definition = segment.definition || { filters: [] };
    const { filters, advancedOptions } = definition;

    // use old syntax for non-compound segments
    if (!filters.length || !isCompoundSegment(segment)) {
        const pipeline = await Promise.resolve(compile(filters, advancedOptions));

        return { pipeline };
    }

    const conditions = buildCompoundPipelines(filters, advancedOptions);
    const compound = createCompoundExpression(conditions.compounds);
    const pipelines = {};

    await Promise.all(
        Object.entries(conditions.inlines).map(async ([key, inline]) => {
            pipelines[key] = await Promise.resolve(compile(inline, advancedOptions));
        }),
    );

    return { pipelines, compound };
}

export function getKinds (conditions, configs) {
    const usesSubAccounts = hasSubscriptionFlag('usesSubaccounts');
    const kinds = uniq(conditions.map((condition) => condition.kind));

    if (kinds.length <= 0) {
        kinds.push('visitor');
    }

    if (!kinds?.includes('visitor') && configs.recurrence) {
        kinds.push('visitor');
    }

    if (kinds?.includes('account') && usesSubAccounts) {
        kinds.push('parentAccount');
    }

    return kinds;
}

function getPollReponseSources (conditions) {
    const keyValueTuples = conditions
        .filter((condition) => {
            return condition.schema === 'poll';
        })
        .map((condition, i) => {
            return [
                pollSourceKey(condition),
                {
                    kind: condition.kind,
                    guideId: condition.guideId,
                    pollId: condition.pollId,
                    response: `pollResponse${i}`,
                    ...(condition.npsSegmentType === 'npsThemes'
                        ? { themeNames: `themeNames${i}` }
                        : {}) /* themeNames key is optional */,
                    time: `pollTime${i}`,
                    filteredByTime: condition.time !== 'ever',
                },
            ];
        });

    const uniquConditionsMap = uniqBy(keyValueTuples, (singleKeyValueTuple) => singleKeyValueTuple[0]).reduce(
        (acc, keyValueTuple) => {
            const [key, aggConfigValue] = keyValueTuple;
            acc[key] = aggConfigValue;

            return acc;
        },
        {},
    );

    return uniquConditionsMap;
}

function getGuides (conditions) {
    return conditions
        .filter((condition) => {
            return condition.type === 'guide';
        })
        .map((condition, i) => {
            condition.field = `lastState${i}`;
            condition.filterIndex = i;

            return condition;
        });
}

function getEmails (conditions) {
    return conditions
        .filter((condition) => {
            return condition.type === 'email';
        })
        .map((condition, i) => {
            condition.type = condition.fact;
            condition.filterIndex = i;

            return condition;
        });
}

function getGuideElements (conditions) {
    return conditions
        .filter((condition) => {
            return condition.schema === 'guideElement';
        })
        .map((condition, i) => {
            condition.field = `guideElement${i}_clicked`;

            return condition;
        });
}

export function getEmailElements (conditions) {
    return conditions
        .filter((condition) => {
            return condition.schema === 'emailElement';
        })
        .map((condition, i) => {
            condition.field = `emailElement${i}_clicked`;
            condition.filterIndex = i;

            return condition;
        });
}

function getFeedbackRequests (conditions) {
    return conditions
        .filter((condition) => {
            return condition.type === 'feedbackRequest';
        })
        .map((condition, i) => {
            condition.field = `feedbackRequest${i}`;

            return condition;
        });
}

function getJourneys (conditions) {
    return conditions
        .filter((condition) => {
            return condition.type === 'journey';
        })
        .map((condition, i) => {
            condition.field = `addedAt${i}`;
            condition.filterIndex = i;

            return condition;
        });
}

function getSurveys (conditions) {
    return conditions.filter((condition) => {
        return condition.type === 'survey';
    });
}

function getUserAgent (conditions) {
    return filter(conditions, (condition) => {
        return condition.type === 'useragent';
    }).map((condition) => {
        const appIdString = getAutoPropAppIdString(condition.appId);
        condition.field = `userAgent${appIdString}.${condition.field}`;

        return condition;
    });
}

function getExperiments (conditions) {
    return filter(conditions, (condition) => {
        return condition.type === 'guideExperiment';
    }).map((condition, idx) => {
        condition.filterIndex = idx;

        return condition;
    });
}

function addSpawnForMultipleSources ({ pipeline, newPipeline, kindId, isMainPipeline }) {
    if (!pipeline.length) {
        return newPipeline;
    }

    const isSpawnNeeded = pipeline[0].source;
    let spawnPipeline = pipeline;
    if (isSpawnNeeded) {
        if (isMainPipeline && !pipeline.some((child) => child.cat)) {
            pipeline.push(o.cat());
        }

        spawnPipeline = o.pipeline(o.spawn(o.pipeline(...pipeline), newPipeline), o.join(kindId));
    } else {
        spawnPipeline[0].spawn.push(newPipeline);
    }

    spawnPipeline[1].join.width = spawnPipeline[0].spawn.length;

    return spawnPipeline;
}

// if `showGuidesToAnon` is defined it indicates that we are building
// a guide audience pipeline and should not depend upon the `shouldShowAnonymous` feature flag
// because that feature flag is specific to analytics
function selectIdentified (pipeline, identified, { showGuidesToAnon, isReplayCaptureSegment } = {}) {
    const hasIdentified = pipeline.some((operator) => operator.identified != null);
    const identifiedWhitelist = /events|guidesSeen|pollsSeen|visitors|timespan/i; // includes pageEvents etc.
    const shouldShowAnonymous = hasSubscriptionFlag('showAnonymous');

    if (!hasIdentified) {
        if (showGuidesToAnon !== undefined) {
            // when `showGuidesToAnon` is false, always identify
            // otherwise, adhere to provided `identified` arg
            if (showGuidesToAnon === false) {
                identified = 'visitorId';
            }
            // If we are building a replay segment we do not need to add the identified filter
            // the backend will handle it
        } else if (!shouldShowAnonymous && !isReplayCaptureSegment) {
            // when `shouldShowAnonymous` is false, always identify
            // otherwise, adhere to provided `identified` arg
            identified = 'visitorId';
        }

        if (
            pipeline[pipeline.length - 1].select &&
            Object.keys(pipeline[pipeline.length - 1].select).some((field) => field === 'visitorId') &&
            identified
        ) {
            // Add the identified filter after the select
            pipeline.push({ identified });

            const crawlPipeline = (aPipeline) => {
                if (isArray(aPipeline)) {
                    return aPipeline.forEach((operator, index) => {
                        if (isArray(operator)) return crawlPipeline(aPipeline[index]);
                        if (Object.keys(aPipeline[index])[0] === 'source') {
                            const sourceKeys = Object.keys(aPipeline[index].source);
                            if (sourceKeys.some((sourceKey) => identifiedWhitelist.test(sourceKey))) {
                                // Add identified right after source before select removes visitorId
                                return aPipeline.splice(1, 0, { identified });
                            }
                        }

                        return crawlPipeline(aPipeline[index]);
                    });
                }

                if (isObject(aPipeline)) {
                    return crawlPipeline(aPipeline[Object.keys(aPipeline)[0]]);
                }
            };
            pipeline.forEach((operator) => {
                // Add the identified filter to all merge pipelines with visitorId (ie visitors, events, *Events)
                if (operator.merge) {
                    // So, sometimes a spawn ends up inside of the merge
                    // and later gets cleaned out, so we can't assume
                    // what index the merge pipeline lives
                    crawlPipeline(operator.merge.pipeline);
                }

                if (operator.spawn) {
                    operator.spawn.forEach((spawnPipeline) => crawlPipeline(spawnPipeline));
                }
            });
        }
    }

    return pipeline;
}

function dedupeAggregations (aggregations) {
    const aggsWithNormalizedAllAppIds = aggregations.map((aggregation) => {
        const appId = !aggregation?.appId || [ALL_PLATFORM, ALL_AGG_ID].includes(aggregation.appId)
            ? ALL_PLATFORM
            : aggregation.appId;

        return { ...aggregation, appId };
    });

    return uniqBy(aggsWithNormalizedAllAppIds, ({ appId, field }) => [appId, field].join('-'));

}

function dedupeFilterPipeline (filterPipeline) {
    const uniqueFilters = new Set();
    const uniqueEvals = new Set();
    const dedupedFilterPipeline = filterPipeline.filter((obj) => {
        if ((obj.evalExpression || obj.eval) && (uniqueEvals.has(obj.evalExpression) || uniqueEvals.has(obj.eval))) return false; // skip duplicate evals
        if (obj.evalExpression) uniqueEvals.add(obj.evalExpression);
        if (obj.eval) uniqueEvals.add(obj.eval);

        if (obj.filter && uniqueFilters.has(obj.filter)) return false; // skip duplicate filters
        if (obj.filter) uniqueFilters.add(obj.filter);

        return true;
    });

    return dedupedFilterPipeline;
}

export function getConditions (filters) {
    return filters
        .map((filter) => filter.or || [filter])
        .flat()
        .filter((filter) => !!filter.kind)
        .map(ListColumn);
}

export function compile (filters, configs = {}) {
    filters = isoDatesToTimestamps(filters);
    const selectedVisitorType = configs ? configs.selectedVisitorType : null;

    const conditions = getConditions(filters);

    const kinds = getKinds(conditions, configs);
    // eslint-disable-next-line no-nested-ternary
    const kind = kinds.includes('visitor') ? 'visitor' : kinds.includes('account') ? 'account' : 'parentAccount';

    // Poll responses
    const pollResponseSources = getPollReponseSources(conditions);
    conditions.forEach((condition) => {
        if (condition.schema === 'poll') {
            const fieldName = condition.npsSegmentType === 'npsThemes' ? 'themeNames' : 'response';
            condition.field = pollResponseSources[pollSourceKey(condition)][fieldName];
        }
    });
    filters = expandTimeBoundPollFilters(filters, pollResponseSources);
    filters = expandNpsResponseGroupFilters(filters, pollResponseSources);

    const surveys = getSurveys(conditions);
    filters = expandTimeBoundSurveyFilters(filters);

    const guides = getGuides(conditions);
    const emails = getEmails(conditions);
    const emailElements = getEmailElements(conditions);
    const guideElements = getGuideElements(conditions);
    const feedbackRequests = getFeedbackRequests(conditions);
    const journeys = getJourneys(conditions);
    const useragent = getUserAgent(conditions);
    const experiments = getExperiments(conditions);
    const sourceRoot = spawnPrimarySource(kinds, kind);
    const mainPipeline = o.pipeline();
    const filterPipeline = o.pipeline();

    // Unwind account IDs if conditions include account or parent account, and primary target is visitor
    if (kind === 'visitor' && kinds.length > 1) {
        mainPipeline.push(
            ...o.pipeline(
                o.evaluate('accountId', 'metadata.auto.accountids'),
                o.unwind('accountId', { keepEmpty: true }) /* because there might not be an account */,
            ),
        );
    }

    if (useragent.length) {
        const appIds = map(uniqBy(useragent, 'appId'), 'appId');
        appIds.forEach((appId) => {
            const appIdString = getAutoPropAppIdString(appId);
            mainPipeline.push(o.userAgent(`userAgent${appIdString}`, `metadata.auto${appIdString}.lastuseragent`));
        });
    }

    // Expand types that are not the primary target
    kinds.forEach((_kind) => {
        let parentAccountIdAdded = false;
        if (_kind !== kind) {
            const hasMetadata = conditions.some((condition) => {
                return condition.kind === _kind && condition.schema !== 'aggregation' && !isListContains(condition);
            });

            if (_kind === 'parentAccount' && !parentAccountIdAdded) {
                mainPipeline.push(o.evaluate('parentAccountId', 'parentAccountId(accountId)'));
                parentAccountIdAdded = true;
            }
            if (hasMetadata) {
                mainPipeline.push(o.bulkExpand(_kind));
            }
        }
    });

    // Create aggregation roots for all types that have aggregation filters
    const aggregationRoot = {};
    aggregationRoot[kind] = sourceRoot;

    kinds.forEach((_kind) => {
        if (_kind !== kind) {
            const hasAggregation = conditions.some((condition) => {
                return condition.kind === _kind && (condition.schema === 'aggregation' || isListContains(condition));
            });
            if (hasAggregation) {
                aggregationRoot[_kind] = o.pipeline();
            }
        }
    });

    const tags = {};
    const tagName = (id) => {
        if (!id) return;
        if (tags[id] == undefined || tags[id] == null) {
            const name = Object.keys(tags).length;
            tags[id] = name;
        }

        return tags[id];
    };

    const allAggregations = conditions
        .filter((condition) => {
            return condition.schema === 'aggregation' || condition.schema === 'counter' || isListContains(condition);
        })
        .map((condition, index) => {
            const unhandledFields = ['pageId', 'tagId', 'featureId', 'trackTypeId', 'guideElementId', 'ideaId', 'utm', 'emailElementId', 'appId'];
            const idKey = unhandledFields.find((key) => key in condition);

            const isSessionPropertyCondition = condition.id === 'utm';
            const tagIndex = isSessionPropertyCondition ? `${condition.name}${tagName(condition[idKey])}` : tagName(condition[idKey]);
            condition.field = fieldName(condition, tagIndex, index);

            return condition;
        });

    const aggregations = dedupeAggregations(allAggregations);

    // Build aggregations
    aggregations.forEach((filter) => {
        const pipeline = aggregationRoot[filter.kind] || o.pipeline();

        if (filter.schema === 'aggregation') {
            const { field, id } = filter;
            const isMainPipeline = kind === filter.kind;
            const kindId = `${filter.kind}Id`;
            const timeSeries = filter.getTimeSeries(configs.timeSeries);

            const filterIsSessionProperty = id === 'utm';

            const fallbackAppId = filterIsSessionProperty ? WEB_LIKE_AGG_ID : ALL_AGG_ID;
            let filterAppId;
            if (filter.appId) {
                filterAppId = isPlatformApp(filter.appId) ? `expandAppIds("${filter.appId}")` : filter.appId;
            }
            const appId = filterAppId || fallbackAppId;

            let child;

            if (filter.pageId) {
                child = o.pipeline(o.sources.pageEvents({ pageId: filter.pageId, timeSeries, appId }));
            } else if (filter.featureId) {
                child = o.pipeline(o.sources.featureEvents({ featureId: filter.featureId, timeSeries, appId }));
            } else if (filter.trackTypeId) {
                child = o.pipeline(o.sources.trackEvents({ trackTypeId: filter.trackTypeId, timeSeries, appId }));
            } else if (filter.guideElementId) {
                child = o.pipeline(
                    o.sources.guideEvents({ guideId: filter.guideId, timeSeries }),
                    o.filter('eventSubType(type) == "guideActivity"'),
                    o.filter(`uiElementId == "${filter.guideElementId}"`),
                );
            } else if (filter.emailElementId) {
                child = o.pipeline(
                    o.sources.emailEvents({
                        appId,
                        timeSeries,
                        targetType: 'campaign',
                        targetId: filter.emailId,
                    }),
                    o.filter('type == "email:pendo:clicked"'),
                    o.unmarshal({
                        eventProperties: 'properties',
                    }),
                    o.evalExpression({
                        elementData: 'lookup(eventProperties, "linkTags")',
                    }),
                    o.evalExpression({
                        elementId: 'lookup(elementData, "dom-id")',
                    }),
                    o.filter(`elementId == "${filter.emailElementId}"`),
                );
            }

            if (!child) {
                child = o.pipeline(o.sources.events({ timeSeries, appId }));
            }

            if (filter.eventPropertyName || (filter.eventPropertyField && has(filter, 'eventPropertyValue'))) {
                const operator = get(
                    filter,
                    'eventPropertyOperator',
                    isBoolean(filter.eventPropertyValue) ? '==' : filter.operator,
                );

                if (get(filter, 'automaticEventProperty')) {
                    child.push(o.userAgent('parsedUA', 'userAgent'));
                }

                if (
                    get(filter, 'eventPropertyField') === 'region' &&
                    get(filter, 'filterType') === FilterType.Location
                ) {
                    child.push(o.evalExpression('country_region', regionEvalExpression));
                }

                child.push(
                    o.filter(
                        createFilterString(
                            { ...filter, operator, value: filter.eventPropertyValue ?? '' },
                            filter.kind,
                            true,
                        ),
                    ),
                );
            }

            let factVal = filter.fact;

            if (filterIsSessionProperty) {
                const filterString = createFilterString(filter, filter.kind);
                child.push(o.filter(filterString));
                factVal = 'eventCount';
            }

            NamedAggregation(child, factVal, kindId, field);

            // Filter out rows with missing `kindId` as they break the aggregation.
            if (filter.kind === 'account' || filter.kind === 'parentAccount') {
                child.push(o.filter(`${kindId} != ""`));
            }

            if (!filterIsSessionProperty) {
                child.push(
                    o.select({
                        [kindId]: kindId,
                        [field]: field,
                    }),
                );
            }

            if (pipeline.length) {
                aggregationRoot[filter.kind] = addSpawnForMultipleSources({
                    pipeline,
                    newPipeline: child,
                    kindId,
                    isMainPipeline,
                });
            } else {
                aggregationRoot[filter.kind] = child;
            }

            // Default aggregation values to zero after join
            filterPipeline.push(o.evalExpression(field, `if(isNil(${field}),0,${field})`));
        } else if (filter.id === 'idea') {
            const visitorVotes = {};

            visitorVotes[filter.field] = {
                vocItemId: filter.ideaId,
            };

            pipeline.push({ visitorVotes });
            pipeline.push(o.unwind(filter.field, { keepEmpty: true }));
        } else if (filter.schema === 'counter') {
            const itemSeen = {};

            itemSeen[filter.field] = {
                [`${filter.type}Id`]: filter[`${filter.type}Id`],
            };

            pipeline.push({ itemSeen });
        } else if (isListContains(filter)) {
            const { field, tagId } = filter;
            const kindId = `${filter.kind}Id`;

            const tagPipeline = o.pipeline(
                o.sources[`${filter.kind}Tags`]({ [`${filter.kind}TagId`]: tagId }), // withAddedAt?
                o.select({
                    [kindId]: `${filter.kind}Id`,
                    [field]: 'true',
                }),
            );

            aggregationRoot[filter.kind] = addSpawnForMultipleSources({
                pipeline,
                newPipeline: tagPipeline,
                kindId,
                isMainPipeline: filter.kind === kind,
            });
        }
    });

    // Build guide aggregations
    guides.forEach((filter) => {
        const lastStateId = `lastState${filter.filterIndex}`;
        const guideLastTimeId = `guideLastTime${filter.filterIndex}`;
        const guideFirstTimeId = `guideFirstTime${filter.filterIndex}`;
        const guideCountId = `guideSeenCount${filter.filterIndex}`;
        const mappings = {
            [lastStateId]: 'lastState',
            [guideLastTimeId]: 'lastSeen',
            [guideFirstTimeId]: 'firstSeen',
            [guideCountId]: 'seenCount',
            visitorId: 'visitorId',
        };

        const child = o.pipeline(
            o.sources.guidesSeenEver({ guideId: filter.guideId }),
            o.group(
                ['visitorId'],
                o.groupField('lastState', { first: 'lastState' }),
                o.groupField('lastSeen', { max: 'lastSeenAt' }),
                o.groupField('firstSeen', { min: 'firstSeenAt' }),
                o.groupField('seenCount', { max: 'seenCount' }),
            ),
            o.select(mappings),
        );

        aggregationRoot[filter.kind] = addSpawnForMultipleSources({
            pipeline: aggregationRoot[filter.kind],
            newPipeline: child,
            kindId: `${filter.kind}Id`,
            isMainPipeline: filter.kind === kind,
        });
    });

    guideElements.forEach((filter) => {
        const child = o.pipeline({
            source: {
                guidesElementClickEver: {
                    guideId: filter.guideId,
                    guideStepId: filter.guideStepId,
                    uiElementId: filter.guideElementId,
                },
            },
        });

        if (filter.clicked === 'withinLast') {
            child.push(o.filter(`lastClickAt >= dateAdd(startOfPeriod("daily", now()),-${filter.count},"days")`));
        } else if (filter.clicked === 'since') {
            child.push(o.filter(`lastClickAt >= ${momentWrapper(filter.value).valueOf()}`));
        }

        child.push(
            ...o.pipeline(
                o.group(['visitorId'], o.groupField('clickCount', { first: 'clickCount' })),
                o.select({
                    visitorId: 'visitorId',
                    [filter.field]: 'clickCount',
                }),
            ),
        );

        aggregationRoot[filter.kind] = addSpawnForMultipleSources({
            pipeline: aggregationRoot[filter.kind],
            newPipeline: child,
            kindId: `${filter.kind}Id`,
            isMainPipeline: filter.kind === kind,
        });
    });

    // Build emails aggregations
    emails.forEach((filter) => {
        if (filter.emailId) {
            const engagementsId = `engagements${filter.filterIndex}`;
            const mappings = {
                visitorId: 'visitorId',
                [engagementsId]: 'engagements',
            };

            const source = { messageEngagementEver: filter.emailId === '*' ? null : { messageId: filter.emailId } };

            const child = o.pipeline({ source }, o.select(mappings));

            aggregationRoot[filter.kind] = addSpawnForMultipleSources({
                pipeline: aggregationRoot[filter.kind],
                newPipeline: child,
                kindId: `${filter.kind}Id`,
                isMainPipeline: filter.kind === kind,
            });
        }
    });

    // Build email elements aggregations
    emailElements.forEach((filter) => {
        const child = o.pipeline({
            source: {
                messageEngagementEver: {
                    messageId: filter.emailId,
                },
            },
        });

        const { emailElementId } = filter;
        const elementEncodedId = `${b64EncodeUnicode(emailElementId)}`;

        child.push(
            o.evalExpression(`elementData${filter.filterIndex}`, `lookup(engagements, "clicked_${elementEncodedId}")`),
        );

        if (filter.clicked === 'withinLast') {
            child.push(
                o.filter(
                    `elementData${filter.filterIndex}.last >= dateAdd(startOfPeriod("daily", now()),-${filter.count},"days")`,
                ),
            );
        } else if (filter.clicked === 'since') {
            child.push(o.filter(`elementData${filter.filterIndex}.last >= ${momentWrapper(filter.value).valueOf()}`));
        }

        child.push(
            ...o.pipeline(
                o.select({
                    visitorId: 'visitorId',
                    [filter.field]: `elementData${filter.filterIndex}.count`,
                }),
            ),
        );

        aggregationRoot[filter.kind] = addSpawnForMultipleSources({
            pipeline: aggregationRoot[filter.kind],
            newPipeline: child,
            kindId: `${filter.kind}Id`,
            isMainPipeline: filter.kind === kind,
        });
    });

    // Build Feedback request voter aggregations
    feedbackRequests.forEach((filter) => {
        const kindId = `${filter.kind}Id`;
        const { appId, fact, field, feedbackRequestId } = filter;

        const child = o.pipeline(
            o.sources.feedbackVotesEver({ appId, requestId: feedbackRequestId }),
            o.filter('visitorId != ""'),
            o.group([kindId], o.groupField('createdAt', { max: 'createdAt' })),
            o.merge(
                [kindId, 'createdAt'],
                {},
                o.pipeline(o.sources.feedbackVotesEver({ appId, requestId: feedbackRequestId })),
            ),
            o.filter('priority > -1'),
        );

        NamedAggregation(child, fact, kindId, field);
        filterPipeline.push(o.evalExpression(field, `if(isNil(${field}),0,${field})`));

        aggregationRoot[filter.kind] = addSpawnForMultipleSources({
            pipeline: aggregationRoot[filter.kind],
            newPipeline: child,
            kindId: `${filter.kind}Id`,
            isMainPipeline: filter.kind === kind,
        });
    });

    // Build poll responses
    Object.values(pollResponseSources).forEach((source) => {
        const child = o.pipeline({
            source: {
                pollsSeenEver: { guideId: source.guideId, pollId: source.pollId },
            },
        });

        const select = o.select({
            [`${source.kind}Id`]: `${source.kind}Id`,
            timestamp: 'time',
            [source.response]: 'response',
        });

        if (source.filteredByTime) {
            select.select[source.time] = 'time';
        }

        child.push(select);

        if (source.themeNames) {
            child.push(
                ...o.pipeline(
                    o.merge(
                        [`${source.kind}Id`, 'timestamp'],
                        {},
                        o.pipeline(
                            {
                                source: {
                                    npsPredictions: {
                                        guideId: source.guideId,
                                    },
                                },
                            },
                            o.select({
                                [`${source.kind}Id`]: `${source.kind}Id`,
                                [source.themeNames]: 'themeNames',
                                timestamp: 'timestamp',
                            }),
                        ),
                    ),
                ),
            );
        }

        aggregationRoot[source.kind] = addSpawnForMultipleSources({
            pipeline: aggregationRoot[source.kind],
            newPipeline: child,
            kindId: `${source.kind}Id`,
            isMainPipeline: source.kind === kind,
        });
    });

    // Build survey aggregations
    surveys.forEach((filter) => {
        if (filter.surveyId) {
            const child = o.pipeline(
                o.sources.pollsSeenEver({
                    guideId: filter.guideId,
                    surveyQuestionId: filter.surveyQuestionId,
                }),
                o.select({
                    accountId: 'accountId',
                    visitorId: 'visitorId',
                    [surveyAggFriendlyQuestionId(filter)]: 'response',
                    [surveyAggFriendlyTime(filter)]: 'time',
                }),
            );

            aggregationRoot[filter.kind] = addSpawnForMultipleSources({
                pipeline: aggregationRoot[filter.kind],
                newPipeline: child,
                kindId: `${filter.kind}Id`,
                isMainPipeline: filter.kind === kind,
            });
        }
    });

    // Build journey aggregations
    journeys.forEach((filter) => {
        const visitorTagId = filter.staticSegmentId;
        const child = o.pipeline(
            o.sources.visitorTags({ visitorTagId, withAddedAt: true }),
            o.evalExpression(`addedAt${filter.filterIndex}`, `lookup(visitorTags, "${visitorTagId}")`),
        );

        aggregationRoot[filter.kind] = addSpawnForMultipleSources({
            pipeline: aggregationRoot[filter.kind],
            newPipeline: child,
            kindId: `${filter.kind}Id`,
            isMainPipeline: filter.kind === kind,
        });
    });

    // build experiments aggregations
    experiments.forEach((filter) => {
        const { experiment, filterIndex, variantName, experimentConverted } = filter;
        if (!experiment || !variantName) return;

        const aggParams = GuideExperimentAggUtils.createAggParamsFromExperiment(experiment);

        const experimentVisitorId = `experimentVisitorId${filterIndex}`;

        const child = GuideExperimentVisitorsAgg().build(aggParams);

        let conversionFilter = null;
        if (experimentConverted === 'Yes') {
            conversionFilter = o.filter('converted == true');
        } else if (experimentConverted === 'No') {
            conversionFilter = o.filter('converted == false');
        }

        child.push(
            ...o.pipeline(
                o.filter(`variant == "${variantName}"`),
                conversionFilter,
                o.select({ [experimentVisitorId]: 'visitorId', 'visitorId': 'visitorId' }),
            ).filter(Boolean),
        );

        aggregationRoot[filter.kind] = addSpawnForMultipleSources({
            pipeline: aggregationRoot[filter.kind],
            newPipeline: child,
            kindId: `${filter.kind}Id`,
            isMainPipeline: filter.kind === kind,
        });
    });

    const segmentPipeline = o.pipeline(aggregationRoot[kind]);
    Object.keys(aggregationRoot).forEach((_kind) => {
        if (_kind !== kind) {
            const id = `${_kind}Id`;

            mainPipeline.push(...o.pipeline(o.merge([id], {}, aggregationRoot[_kind])));
        }
    });

    filters.forEach((filter) => {
        if (filter.segmentId) {
            const agg = o.pipeline(
                o.evalExpression('accountId', 'metadata.auto.accountids'),
                o.unwind('accountId', { keepEmpty: true }),
            );

            if (!isDefaultSegment(filter.segmentId)) {
                agg.push(o.segment(filter.segmentId));
            }

            filterPipeline.push(...agg);
        }

        // Session properties have an initial filter applied on the event source.
        // We don't want to duplicate that same filter, so we need a custom filter at this step.
        const needsCustomFilterString = true;
        const filterString = createEntireFilterString(filter, kind, needsCustomFilterString);
        if (filterString?.length) {
            filterPipeline.push(o.filter(filterString));
        }
    });

    const select = {};

    kinds.forEach((kind) => {
        select[`${kind}Id`] = `${kind}Id`;
    });

    filterPipeline.push(o.select(select));
    const dedupedFilterPipeline = dedupeFilterPipeline(filterPipeline);

    const { showGuidesToAnon, isReplayCaptureSegment } = configs;
    segmentPipeline.push(...mainPipeline, ...dedupedFilterPipeline);

    selectIdentified(segmentPipeline, selectedVisitorType, {
        showGuidesToAnon,
        isReplayCaptureSegment,
    });

    return segmentPipeline;
}
